import React, { ReactNode, useCallback } from 'react'

import { RecipeAlternativeOptions } from './RecipeAlternativeOptions'
import { cssFineDineInButton } from './styles'
import { useDeviceType, DeviceType } from '@library/dom'
import { AlternativeOptions } from '../../model/alternativeOptions/alternativeOptions'
import { queueTrackingEvent } from '@library/tracking-integration'
import {
  CollectionUUID,
  isRecipeFineDineIn,
  Recipe,
  RecipeCoreId,
  SupportedOptionType,
} from '@library/menu-service'
import {
  DesktopCollapsable,
  MobileCollapsable,
} from './SwapOptionsCollapsable/SwapOptionsCollapsable'
import {
  Box,
  Color,
  DisplayPosition,
  FontFamily,
  JustifyContent,
  Text,
} from '@gousto-internal/citrus-react'

export type TrackRecipeAlternativeOptionsMenuOpen = {
  action: 'recipe-alternative-options-menu-open'
  recipeId: string
  collectionId: string
}

export type TrackRecipeAlternativeOptionsMenuSwapRecipes = {
  action: 'recipe-alternative-options-menu-swap-recipes'
  previousRecipeId: string
  nextRecipeId: string
  collectionId: string
}

const alternativeOptionsStrategyMap: Record<
  SupportedOptionType,
  (
    isMobile: boolean,
    currentRecipeCoreId: RecipeCoreId,
    currentCollectionId: CollectionUUID,
    alternativeOptions: AlternativeOptions,
    isLoading: boolean,
    selectVariant: (variant: { variant: Recipe; variantOutOfStock: boolean }) => void,
    close: () => void,
  ) => ReactNode
> = {
  none: () => null,
  ingredients: () => null,
  recipes: (
    isMobile,
    currentRecipeCoreId,
    currentCollectionId,
    alternativeOptions,
    isLoading,
    selectVariant,
    close,
  ) => (
    <RecipeAlternativeOptions
      currentRecipeCoreId={currentRecipeCoreId}
      onChangeCheckedRecipe={({ previousRecipeId, nextRecipeId }) => {
        queueTrackingEvent<TrackRecipeAlternativeOptionsMenuSwapRecipes>({
          action: 'recipe-alternative-options-menu-swap-recipes',
          previousRecipeId,
          nextRecipeId,
          collectionId: currentCollectionId,
        })
        close()
      }}
      selectVariant={selectVariant}
      options={alternativeOptions.options}
      isMobile={isMobile}
      isLoading={isLoading}
    />
  ),
}

export function SwapAlternativeOptions({
  currentRecipeCoreId,
  currentCollectionId,
  recipe,
  alternativeOptions,
  isLoading,
  selectVariant,
  onOpenAlternativeOptions,
}: {
  currentRecipeCoreId: RecipeCoreId
  currentCollectionId: CollectionUUID
  recipe: Recipe
  alternativeOptions: AlternativeOptions
  isLoading: boolean
  selectVariant: ({
    variant,
    variantOutOfStock,
  }: {
    variant: Recipe
    variantOutOfStock: boolean
  }) => void
  onOpenAlternativeOptions?: () => void
}) {
  const deviceType = useDeviceType()
  const isFineDineIn = isRecipeFineDineIn(recipe)

  const onOpen = useCallback(() => {
    queueTrackingEvent<TrackRecipeAlternativeOptionsMenuOpen>({
      action: 'recipe-alternative-options-menu-open',
      recipeId: currentRecipeCoreId,
      collectionId: currentCollectionId,
    })
    if (onOpenAlternativeOptions) {
      onOpenAlternativeOptions()
    }
  }, [currentCollectionId, currentRecipeCoreId, onOpenAlternativeOptions])

  const alternativeOptionsStrategy = useCallback(
    ({ close, isMobile }: { close: () => void; isMobile: boolean }) => {
      const strategy =
        alternativeOptionsStrategyMap[alternativeOptions.optionsType] ||
        alternativeOptionsStrategyMap.none

      return strategy(
        isMobile,
        currentRecipeCoreId,
        currentCollectionId,
        alternativeOptions,
        isLoading,
        selectVariant,
        close,
      )
    },
    [currentCollectionId, currentRecipeCoreId, selectVariant, alternativeOptions, isLoading],
  )

  if (alternativeOptions.optionsType === 'none') {
    return null
  }

  if (alternativeOptions.optionsType === 'ingredients') {
    return (
      <Box position={DisplayPosition.Absolute} bottom="0" right="0" width="100%">
        <Box display="flex" justifyContent={JustifyContent.FlexEnd} style={{ marginLeft: '1rem' }}>
          <Box>
            <Text fontFamily={FontFamily.Bold} color={Color.Secondary_400}>
              Options
            </Text>
          </Box>
        </Box>
      </Box>
    )
  }

  if (deviceType === DeviceType.MOBILE) {
    return <MobileCollapsable onOpen={onOpen}>{alternativeOptionsStrategy}</MobileCollapsable>
  } else {
    return (
      <DesktopCollapsable
        onOpen={onOpen}
        buttonStyles={isFineDineIn ? cssFineDineInButton : undefined}
      >
        {alternativeOptionsStrategy}
      </DesktopCollapsable>
    )
  }
}
